import styled from "styled-components";

export const PageWrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: calc(100vh - 80px);
  background: var(--mp-bodyBackground, #f7f8fc);
`;

/* ─── SIDEBAR ─── */
export const Sidebar = styled.aside`
  width: 260px;
  flex-shrink: 0;
  border-right: 1px solid rgba(23, 23, 23, 0.08);
  padding: 32px 24px;
  background: var(--mp-bodyBackground, #f7f8fc);

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255, 255, 255, 0.06);
  }

  @media (max-width: 991px) {
    display: none;
  }
`;

export const SidebarTitle = styled.h4`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--mp-featureDescColor, #434655);
  margin: 0 0 24px 0;
  opacity: 0.7;
`;

export const FilterGroup = styled.div`
  margin-bottom: 32px;
`;

export const FilterGroupTitle = styled.h5`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--mp-headingColor, #171717);
  margin: 0 0 16px 0;
`;

export const FilterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-family: 'Liberation Serif', serif;
  font-size: 14px;
  color: var(--mp-featureDescColor, #434655);
  
  &:hover {
    color: var(--mp-headingColor, #171717);
  }

  input {
    cursor: pointer;
    width: 16px;
    height: 16px;
    accent-color: var(--mp-featureIconColor, #D863BA);
    
    [data-marketplace-theme="dark"] &,
    .dark & {
      accent-color: #FFC951;
    }
  }
`;

/* ─── MAIN CONTENT ─── */
export const MainContent = styled.main`
  flex: 1;
  padding: 32px 40px 80px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 24px 20px 60px;
  }

  @media (max-width: 480px) {
    padding: 16px 12px 40px;
  }
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    margin-bottom: 16px;
    gap: 12px;
  }
`;

export const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
  }
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-family: 'Liberation Serif', serif;
  font-size: 32px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);

  @media (max-width: 768px) {
    font-size: 24px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

export const ResultBadge = styled.span`
  background: rgba(23, 23, 23, 0.05);
  padding: 6px 12px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: var(--mp-featureDescColor, #434655);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: rgba(255, 255, 255, 0.05);
  }
`;

export const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  justify-content: flex-end;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 10px;
  }
`;

export const SearchInputWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 320px;

  @media (max-width: 768px) {
    max-width: 100%;
    flex: 1;
  }

  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: var(--mp-featureDescColor, #434655);
    opacity: 0.6;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px 10px 40px;
  border-radius: 8px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: var(--mp-cardBackground, #ffffff);
  color: var(--mp-headingColor, #171717);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  outline: none;

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255, 255, 255, 0.1);
  }

  &:focus {
    border-color: var(--mp-featureIconColor, #D863BA);
  }
`;

export const ToggleGroup = styled.div`
  display: flex;
  background: var(--mp-cardBackground, #ffffff);
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 8px;
  overflow: hidden;

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

export const ToggleBtn = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${p => p.$active ? "var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%))" : "transparent"};
  color: ${p => p.$active ? "#ffffff" : "var(--mp-featureDescColor, #434655)"};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  [data-marketplace-theme="dark"] & {
    background: ${p => p.$active ? "linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%)" : "transparent"};
    color: ${p => p.$active ? "#402D00" : "var(--mp-featureDescColor, #434655)"};
  }

  &:hover {
    opacity: ${p => p.$active ? 1 : 0.8};
  }

  @media (max-width: 480px) {
    flex: 1;
    font-size: 10px;
    padding: 8px 6px;
    text-align: center;
  }
`;

export const SortSelectWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--mp-featureDescColor, #434655);

  select {
    padding: 8px 12px;
    border-radius: 8px;
    border: none;
    background: transparent;
    font-weight: 700;
    color: var(--mp-headingColor, #171717);
    outline: none;
    cursor: pointer;
  }
`;

/* ─── GRID ─── */
export const EquipmentGrid = styled.div<{ $viewMode: "grid" | "list" }>`
  display: grid;
  grid-template-columns: ${p => p.$viewMode === "list" ? "1fr" : "repeat(3, 1fr)"};
  gap: 24px;
  align-items: stretch;

  @media (max-width: 1200px) {
    grid-template-columns: ${p => p.$viewMode === "list" ? "1fr" : "repeat(2, 1fr)"};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/* ─── CARD ─── */
export const EquipCard = styled.div<{ $viewMode: "grid" | "list" }>`
  border-radius: 16px;
  overflow: hidden;
  background: var(--mp-cardBackground, #ffffff);
  border: 1px solid rgba(23,23,23,0.06);
  display: flex;
  flex-direction: ${p => p.$viewMode === "list" ? "row" : "column"};
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #15181b;
    border: 1px solid rgba(255,255,255,0.06);
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.08);
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const EquipCardImageWrap = styled.div<{ $viewMode: "grid" | "list" }>`
  width: ${p => p.$viewMode === "list" ? "280px" : "100%"};
  aspect-ratio: ${p => p.$viewMode === "list" ? "1.2" : "1.33"};
  position: relative;
  overflow: hidden;
  background: var(--mp-cardBackground, #f0f0f0);
  flex-shrink: 0;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #1e2226;
  }

  @media (max-width: 768px) {
    width: 100%;
    aspect-ratio: 1.33;
  }
`;

export const EquipCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const TopBadges = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
`;

export const ConditionBadge = styled.span<{ $isNew?: boolean }>`
  padding: 4px 10px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: ${p => p.$isNew ? "var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%))" : "rgba(255,255,255,0.9)"};
  color: ${p => p.$isNew ? "#ffffff" : "#171717"};

  [data-marketplace-theme="dark"] & {
    background: ${p => p.$isNew ? "linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%)" : "rgba(255,255,255,0.9)"};
    color: ${p => p.$isNew ? "#402D00" : "#171717"};
  }
`;

export const VendorBadge = styled.span`
  padding: 4px 10px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  background: #ffffff;
  color: #171717;
  display: flex;
  align-items: center;
  gap: 4px;

  svg { width: 12px; height: 12px; color: var(--mp-featureIconColor, #D863BA); }

  [data-marketplace-theme="dark"] & {
    background: rgba(0,0,0,0.6);
    color: #ffffff;
    backdrop-filter: blur(4px);
    border: 1px solid rgba(255,255,255,0.2);
    
    svg { color: #FFC951; }
  }
`;

export const FavBtn = styled.button<{ $saved?: boolean }>`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${p => p.$saved ? "linear-gradient(135deg, #ff6b6b 0%, #c94daa 100%)" : "rgba(255,255,255,0.9)"};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${p => p.$saved ? "#ffffff" : "#171717"};
  transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  box-shadow: ${p => p.$saved ? "0 2px 12px rgba(201,77,170,0.45)" : "none"};

  svg {
    fill: ${p => p.$saved ? "#ffffff" : "none"};
    transition: fill 0.2s ease;
  }

  &:hover {
    background: ${p => p.$saved ? "linear-gradient(135deg, #ff5252 0%, #b03d97 100%)" : "#ffffff"};
    transform: scale(1.12);
    box-shadow: 0 4px 16px rgba(201,77,170,0.4);
  }

  &:active {
    transform: scale(0.93);
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: ${p => p.$saved ? "linear-gradient(135deg, #ff6b6b 0%, #c94daa 100%)" : "rgba(0,0,0,0.5)"};
    color: #ffffff;
    backdrop-filter: blur(4px);
    border: ${p => p.$saved ? "none" : "1px solid rgba(255,255,255,0.2)"};
    svg { fill: ${p => p.$saved ? "#ffffff" : "none"}; }
  }
`;

export const EquipCardBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;

  @media (max-width: 480px) {
    padding: 14px;
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

export const EquipTitle = styled.h3`
  margin: 0;
  font-family: 'Liberation Serif', serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: var(--mp-headingColor, #171717);
`;

export const RatingScore = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);

  svg { width: 12px; height: 12px; color: var(--mp-featureIconColor, #D863BA); fill: var(--mp-featureIconColor, #D863BA); }

  [data-marketplace-theme="dark"] & svg {
    color: #FFC951; fill: #FFC951;
  }
`;

export const EquipDesc = styled.p`
  margin: 0 0 24px 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: var(--mp-featureDescColor, #434655);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  margin-top: auto;
  margin-bottom: 20px;

  @media (max-width: 480px) {
    gap: 16px;
    margin-bottom: 14px;
  }
`;

export const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PriceLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--mp-featureDescColor, #434655);
  opacity: 0.6;
`;

export const PriceValue = styled.span`
  font-family: 'Liberation Serif', serif;
  font-weight: 700;
  font-size: 20px;
  color: var(--mp-headingColor, #171717);
`;

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ActionBtnPrimary = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
  color: var(--mp-heroBtnColor, #ffffff);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    color: #402D00;
  }

  &:hover { opacity: 0.9; }
`;

export const ActionBtnSecondary = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  background: transparent;
  border: 1px solid rgba(23,23,23,0.15);
  color: var(--mp-headingColor, #171717);

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255,255,255,0.15);
  }

  &:hover {
    border-color: var(--mp-headingColor, #171717);
  }
`;

/* ─── BANNER ─── */
export const BannerBox = styled.div<{ $viewMode: "grid" | "list", $bgUrl: string }>`
  grid-column: ${p => p.$viewMode === "list" ? "1 / -1" : "span 2"};
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  background: #0f1114 url(${p => p.$bgUrl}) no-repeat center center;
  background-size: cover;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px;
  color: #ffffff;
  min-height: 400px;

  @media (max-width: 991px) {
    grid-column: 1 / -1;
  }

  @media (max-width: 480px) {
    padding: 24px;
    min-height: 280px;
  }
`;

export const BannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(11,12,13,0.9) 0%, rgba(11,12,13,0.4) 100%);
`;

export const BannerContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 360px;

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

export const BannerEyebrow = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 16px;
  opacity: 0.8;
`;

export const BannerTitle = styled.h2`
  margin: 0 0 16px 0;
  font-family: 'Liberation Serif', serif;
  font-size: 40px;
  line-height: 48px;
  font-weight: 700;

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

export const BannerDesc = styled.p`
  margin: 0 0 32px 0;
  font-family: 'Liberation Serif', serif;
  font-size: 15px;
  line-height: 24px;
  opacity: 0.8;
`;

export const BannerBtn = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
  color: #ffffff;

  [data-marketplace-theme="dark"] & {
    background: #000000;
    color: #FFC951;
    border: 1px solid #FFC951;
  }

  &:hover { opacity: 0.9; }
`;

export const BannerFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 48px;
  font-family: Montserrat, sans-serif;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  opacity: 0.4;
  
  &::before, &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #ffffff;
    opacity: 0.2;
  }
`;
